// Tenant / hunt / anchor config — the white-label axis. Everything a venue,
// client, or scene needs is data here; new clients ship with zero code.
// Served from /tenants/* (same origin in v1; DO Spaces CDN later — keep
// loaders origin-relative until then).
import {z} from 'zod'

export const ThemeSchema = z.object({
  bg: z.string().default('#0a0a12'),
  fg: z.string().default('#eae6ff'),
  accent: z.string().default('#8affc1'),
  accent2: z.string().default('#b18aff'),
})

export const TenantSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  name: z.string(),
  tagline: z.string().optional(),
  theme: ThemeSchema.prefault({}),
  logoUrl: z.string().optional(),
  copy: z
    .object({
      landingIntro: z.string().optional(),
      coaching: z.string().optional(),
    })
    .prefault({}),
  huntIds: z.array(z.string()).default([]),
})

export const HuntStepSchema = z.object({
  anchorId: z.string(),
  clue: z.object({
    title: z.string().optional(),
    text: z.string(),
    venueUrl: z.string().optional(),
  }),
})

export const HuntSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  name: z.string(),
  steps: z.array(HuntStepSchema).min(1),
  completion: z
    .object({
      text: z.string(),
      ctaUrl: z.string().optional(),
    })
    .optional(),
})

export const AnchorSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  qr: z
    .object({
      /** Canonical sticker URL; scanned payloads are matched by pathname. */
      payload: z.string(),
      sizeM: z.number().positive().default(0.0508),
    }),
  surfaceHint: z.enum(['auto', 'wall', 'table', 'floor']).default('auto'),
  allowFallback: z.boolean().default(true),
  placement: z
    .object({
      offset: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0]),
      rotationDeg: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0]),
      scale: z.number().positive().default(1),
    })
    .prefault({}),
  growth: z
    .object({
      seed: z.number().int().default(1337),
      palette: z.string().default('bioluminescent'),
      density: z.number().min(0).max(1).default(0.5),
      spreadM: z.number().positive().default(2.5),
    })
    .prefault({}),
  hero: z
    .object({
      type: z.enum(['none', 'knot', 'mandala', 'swarm', 'glb']).default('none'),
      url: z.string().optional(),
      sizeM: z.number().positive().default(0.5),
      offset: z.tuple([z.number(), z.number(), z.number()]).default([0, 0, 0.45]),
      loopS: z.number().positive().default(24),
      palette: z.string().optional(),
      beatBpm: z.number().positive().optional(),
    })
    .prefault({}),
  /** Environment proxies authored in install mode (?install): invisible
   *  volumes real objects occupy — vines wrap them, occluders hide behind. */
  proxies: z
    .array(
      z.object({
        kind: z.enum(['cylinder', 'box']),
        center: z.tuple([z.number(), z.number(), z.number()]),
        size: z.tuple([z.number(), z.number(), z.number()]),
      }),
    )
    .default([]),
  mood: z
    .object({
      enabled: z.boolean().default(true),
      /** Brimstone is OPT-IN (field feedback: movement shouldn't punish).
       *  Venues that want the dark turn enable it deliberately. */
      brimstone: z.boolean().default(false),
    })
    .prefault({}),
  /** Interactive beacons: tappable markers that open an info card — a menu,
   *  a note, the story of what you're looking at. Locale-special things. */
  beacons: z
    .array(
      z.object({
        offset: z.tuple([z.number(), z.number(), z.number()]),
        title: z.string(),
        text: z.string(),
        url: z.string().optional(),
        linkLabel: z.string().optional(),
      }),
    )
    .default([]),
  /** Room dressing (jungle theme v1; more themes later). */
  room: z
    .object({
      floor: z.boolean().default(true),
      heavens: z.boolean().default(true),
      creatureCount: z.number().int().min(0).default(2),
    })
    .prefault({}),
})

export type TenantConfig = z.infer<typeof TenantSchema>
export type HuntConfig = z.infer<typeof HuntSchema>
export type AnchorConfig = z.infer<typeof AnchorSchema>

export interface Route {
  tenantId: string
  anchorId: string
}

export interface SceneBundle {
  tenant: TenantConfig
  anchor: AnchorConfig
  /** Hunt containing this anchor, if any, plus the step index. */
  hunt: HuntConfig | null
  stepIndex: number
}

/** Parse /x/{tenant}/{anchor}; in dev, bare / falls back to demo/001. */
export function parseRoute(pathname: string): Route | null {
  const match = /^\/x\/([\w-]+)\/([\w-]+)\/?$/.exec(pathname)
  if (match) return {tenantId: match[1]!, anchorId: match[2]!}
  if (import.meta.env.DEV && pathname === '/') {
    console.info('[route] dev fallback -> /x/demo/001')
    return {tenantId: 'demo', anchorId: '001'}
  }
  return null
}

async function fetchJson(path: string): Promise<unknown> {
  const res = await fetch(path, {cache: 'no-cache'})
  if (!res.ok) throw new Error(`${path}: HTTP ${res.status}`)
  return res.json()
}

export async function loadSceneBundle({tenantId, anchorId}: Route): Promise<SceneBundle> {
  const base = `/tenants/${tenantId}`
  const [tenantRaw, anchorRaw, overlay] = await Promise.all([
    fetchJson(`${base}/tenant.json`),
    fetchJson(`${base}/anchors/${anchorId}.json`),
    // Server-side overlay: live content injection (new menu specials, swapped
    // beacons, tuned growth) without a redeploy. Absent/unreachable -> {}.
    fetchJson(`/api/overlay/${tenantId}/${anchorId}`).catch(() => ({})),
  ])
  const tenant = TenantSchema.parse(tenantRaw)
  const anchor = AnchorSchema.parse({...(anchorRaw as object), ...(overlay as object)})

  let hunt: HuntConfig | null = null
  let stepIndex = -1
  for (const huntId of tenant.huntIds) {
    try {
      const h = HuntSchema.parse(await fetchJson(`${base}/hunts/${huntId}.json`))
      const idx = h.steps.findIndex((s) => s.anchorId === anchorId)
      if (idx !== -1) {
        hunt = h
        stepIndex = idx
        break
      }
    } catch (error) {
      console.warn(`[config] hunt ${huntId} failed to load`, error)
    }
  }
  return {tenant, anchor, hunt, stepIndex}
}

/** Does a scanned QR payload belong to this anchor? Match by URL pathname so
 *  dev (LAN IP) and prod (real domain) stickers both work. */
export function payloadMatchesAnchor(payload: string, anchor: AnchorConfig, route: Route): boolean {
  const expected = `/x/${route.tenantId}/${route.anchorId}`
  try {
    return new URL(payload).pathname.replace(/\/$/, '') === expected
  } catch {
    return payload === anchor.qr.payload
  }
}
